import { Link } from 'react-router-dom'
import { Card } from '../components/ui/Card'
import { Pill } from '../components/ui/Pill'
import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/EmptyState'
import { SectionLabel } from '../components/ui/SectionLabel'
import { useToast } from '../contexts/ToastContext'
import { usePerson } from '../contexts/PersonContext'
import { useReports, useBlocks, useRemoveBlock } from '../hooks/useSafety'
import type { ReportDto } from '../types/community'

function statusPill(status: ReportDto['status']) {
  if (status === 'Resolved') return <Pill tone="moss">Resolved</Pill>
  if (status === 'NoActionNeeded') return <Pill tone="neutral">No action needed</Pill>
  return <Pill tone="honey">Queued for review</Pill>
}

/** Safety centre (COTC_SafetyKudos R4 / DESIGN §3.16), parent role. */
export function SafetyCentrePage() {
  const { flash } = useToast()
  const { people, accountHolderId } = usePerson()
  const reports = useReports()
  const blocks = useBlocks()
  const removeBlock = useRemoveBlock()

  const firstManaged = people.find((p) => p.id !== accountHolderId)?.firstName
  const reportRows = reports.data ?? []
  const blockRows = blocks.data ?? []

  async function unblock(id: string, name: string) {
    try {
      await removeBlock.mutateAsync(id)
      flash(`Unblocked. Your family can see ${name} in supported community views again.`)
    } catch {
      flash('That did not go through — try again.')
    }
  }

  return (
    <div className="animate-rise px-4 py-6 sm:px-10 sm:py-9">
      <div className="font-mono text-[12px] uppercase tracking-label text-ink-fainter">Safety centre</div>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-tight tracking-[-.015em] text-ink">Safety centre</h1>
      <p className="mt-3 max-w-[64ch] text-[15px] leading-relaxed text-ink-muted">
        {firstManaged
          ? `Reports your family has made, including ${firstManaged}’s, and the players your family has blocked. Track your reports and their outcomes here.`
          : 'Reports your family has made and the players you have blocked. Track your reports and their outcomes here.'}
      </p>

      <Card className="mt-7" padding="none">
        <div className="border-b border-line-row px-5 py-4"><SectionLabel>Reports</SectionLabel></div>
        {reports.isLoading ? (
          <p className="px-5 py-6 font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
        ) : reports.isError ? (<div role="alert" className="px-5 py-4 text-[13px] text-honey-fg">Could not load reports. <Button variant="outline" size="sm" onClick={() => void reports.refetch()}>Retry reports</Button></div>) : reportRows.length === 0 ? (
          <div className="px-5 py-3">
            <EmptyState description="No reports from your family yet. Reports submitted by anyone on this account and their outcomes appear here." />
          </div>
        ) : (
          <ul className="divide-y divide-line-row">
            {reportRows.map((r) => (
              <li key={r.id} className="px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[14px] font-medium text-ink">{r.title}</div>
                  {statusPill(r.status)}
                </div>
                <div className="mt-1 flex items-center gap-3">
                  <span className="text-[13px] text-ink-muted">{r.detail}</span>
                  <span className="font-mono text-[10px] uppercase tracking-label text-ink-fainter">{r.whenText}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="mt-6" tone="panel" padding="none">
        <div className="border-b border-line-row px-5 py-4"><SectionLabel>Blocked, quietly</SectionLabel></div>
        <p className="px-5 pt-3 text-[13px] leading-relaxed text-ink-muted">
          Your family no longer sees blocked players in supported community views or rooms they host. Blocking changes your family’s experience; players are not notified.
        </p>
        {blocks.isLoading ? (
          <p className="px-5 py-6 font-mono text-[11px] uppercase tracking-label text-ink-faint">Loading…</p>
        ) : blocks.isError ? (<div role="alert" className="px-5 py-4 text-[13px] text-honey-fg">Could not load blocks. <Button variant="outline" size="sm" onClick={() => void blocks.refetch()}>Retry blocks</Button></div>) : blockRows.length === 0 ? (
          <div className="px-5 py-3">
            <EmptyState description="Nobody is blocked. If you block someone from a report or a room, they appear here." />
          </div>
        ) : (
          <ul className="divide-y divide-line-row">
            {blockRows.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
                <div>
                  <div className="text-[14px] font-medium text-ink">{b.name}</div>
                  <div className="text-[12px] text-ink-muted">{b.why}</div>
                </div>
                <Button variant="outline" size="sm" disabled={removeBlock.isPending} onClick={() => unblock(b.id, b.name)}>Unblock</Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="mt-6 flex flex-wrap gap-4 text-[13px]">
        <Link to="/onboard" className="font-medium text-terra-deep hover:text-terra-hover">Add a managed profile</Link>
        <Link to="/verification" className="font-medium text-terra-deep hover:text-terra-hover">Parent verification, billing &amp; withdrawal</Link>
      </div>
    </div>
  )
}
